/**
 * `/e/<eventId>` on a phone — the donor's event screen (GH-771, GH-781).
 *
 * ## What this page is for
 *
 * The twenty minutes somebody is standing in a hall holding a phone. Everything the room is doing
 * is on one screen and stays there: the total climbing, whether *you* are here, every open prompt,
 * a way to react, and the one green button. Acting on a poll must not take the total off screen,
 * which is the whole reason the prompts expand in place instead of pushing a second view — see
 * `EventActionCard`.
 *
 * ## The address, and the split that shares it
 *
 * `/e/<eventId>` used to be the live display, unconditionally. It is now this page, and
 * `/e/<eventId>?projector=1` is the hall's. One address, two audiences, deliberately: a link an
 * operator forwards to a donor still opens something useful. They cannot be one bundle — the
 * display is an identifier-free, donor-less projection by construction (CLAUDE.md, SPEC.md §7.2),
 * and giving it a donor session to satisfy a route would spend the property that makes it safe to
 * hang on a wall. See `bundleFor` in `infra/static-server.mjs`.
 *
 * ## Three rules this page must not lose
 *
 * **It mints nothing until Give is pressed.** `fetchEventPage` and `fetchGoal` are public reads;
 * `fetchDonationLink` signs a checkout context and is called from the button handler and nowhere
 * else. Opening this page and leaving must cost nothing on the server — the same promise
 * `OrgLanding.tsx` states in its own header.
 *
 * **Giving never asks who you are.** Check-in is the only thing on this screen that needs an
 * identity. The founding promise is that a first-time donor completes a gift without an account
 * (CLAUDE.md), and the Give button is therefore not gated on a session, a check-in, or a name.
 *
 * **No control the server would refuse.** No Give for an organization that cannot accept
 * donations or an event that is not `published`/`live` (`assertAcceptingDonations`); no check-in
 * or prompts unless the event is `live` (the engagement route 404s otherwise); no tickets card
 * without a purchasable tier. Every one of these is the server's answer, read from a payload,
 * never a guess made here.
 *
 * ## Two failure surfaces, kept apart
 *
 * A failed poll does not erase the last good total: the number stays with its `asOf` beside it,
 * because a figure that admits it is forty seconds behind is better than a hall with no figure at
 * all (iOS `EventModel.refreshGoal` states the same rule). Only a failure on the *first* load
 * blanks the page. And a refused prompt action renders next to its own card rather than as a
 * page-level error, so a rate-limited reaction cannot look like the event breaking.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  formatMoney,
  type DonorSessionResponse,
  type EngagementPromptWire,
  type EventPageResponse,
  type GoalResponse,
} from "@givehere/contracts";
import {
  ApiError,
  createPledge,
  type PledgeContact,
  createQuote,
  fetchDonationLink,
  fetchEventPage,
  fetchGoal,
} from "./api.js";
import { AppealCard } from "./AppealCard.js";
import { recalledAppealResponse, rememberAppealResponse } from "./appeal-response.js";
import { checkoutHints, readCheckoutHints } from "./give-hints.js";
import { echoes } from "./copy-echo.js";
import {
  DonorApiError,
  donorCheckIn,
  donorCheckOut,
  fetchDonorSession,
  fetchDonorTicketedEventIds,
  fetchEngagement,
  storedToken,
} from "./donor-session.js";
import { EventOpensAtEvent, isEventAccessRequired } from "./EventAccess.js";
import { EventActionCard } from "./EventActionCard.js";
import { isPhoneVerificationRequired } from "./VerifyMobile.js";
import { EventJoinCard, useEventJoin } from "./EventJoin.js";
import { ShareIconButton } from "./ShareControls.js";
import { Speakers } from "./Speakers.js";
import { Explain } from "./Explain.js";
import { GoalBar } from "./GoalBar.js";
import { headline } from "./goal-math.js";
import {
  CommentIcon,
  InfoIcon,
  LocationIcon,
  PersonIcon,
  PollIcon,
  QuestionIcon,
  TicketIcon,
} from "./icons.js";
import { ONLINE_LINK_REL, drawableOnlineUrl } from "./online-link.js";
import { OrgInfoPanel } from "./OrgInfoPanel.js";
import { OrgMark } from "./OrgMark.js";
import { PromptView } from "./PromptView.js";
import { ReactionBar } from "./ReactionBar.js";
import { ReportMenu } from "./report/ReportMenu.js";
import { ReportNotice } from "./report/ReportNotice.js";
import { useReporting } from "./report/useReporting.js";
import { TriviaGate } from "./TriviaGate.js";

/**
 * Four seconds for the money, fifteen for the list of prompts.
 *
 * The goal matches `POLL_MS` in `live-display/Display.tsx` and `EventModel.refreshInterval` on
 * iOS, so a donor looking from this phone to the projector at the back of the hall is never more
 * than one tick out of step with it. Prompts move at organizer speed — somebody opens a poll
 * between speeches — and asking four times as often would buy nothing a person could perceive
 * while quadrupling the load a full hall puts on one venue's wifi.
 */
const GOAL_POLL_MS = 4000;
const PROMPT_POLL_MS = 15000;

/** Statuses the server will actually take a gift for (`events.assertAcceptingDonations`). */
const GIVABLE_STATUSES = new Set(["published", "live"]);

/** Statuses with nothing left to offer: the total is final and every control is gone. */
const FINISHED_STATUSES = new Set(["ended", "archived", "suspended"]);

/** `access`: membership is on and this event is not open to this visitor (GH-871b). */
type LoadFailure = "not_found" | "ended" | "unavailable" | "access";

export function EventPage({
  eventId,
  joinToken = null,
}: {
  eventId: string;
  /** The big screen's auto-invite, when a QR brought the donor here with `?join=` (GH-864e). */
  joinToken?: string | null;
}) {
  const eventJoin = useEventJoin(joinToken);
  const [page, setPage] = useState<EventPageResponse | null>(null);
  const [goal, setGoal] = useState<GoalResponse | null>(null);
  /*
    The money read's own failure, kept out of `loadFailure` on purpose.

    These are two different questions — "does this event exist" and "is the total reachable right
    now" — and only the first one can empty the screen. Folding the second into the first meant a
    single failed first poll replaced a perfectly good page (organization, event, tickets, the way
    back, all already loaded) with a dead card, and because `loadFailure` is never cleared, the
    page stayed dead through every later poll that succeeded.
  */
  const [goalFailure, setGoalFailure] = useState(false);
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [prompts, setPrompts] = useState<readonly EngagementPromptWire[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [session, setSession] = useState<DonorSessionResponse | null>(null);
  const [openPromptId, setOpenPromptId] = useState<string | null>(null);
  const [presenceBusy, setPresenceBusy] = useState(false);
  const [presenceFailure, setPresenceFailure] = useState<string | null>(null);
  const [giving, setGiving] = useState(false);
  const [giveFailure, setGiveFailure] = useState<string | null>(null);
  /**
   * Whether this tab already answered the appeal (GH-937), read at mount. Checkout records the
   * answer when it lands; the one-tap pledge below (owner 2026-10-10) records it right here —
   * either way the card then acknowledges instead of asking again this session.
   */
  const [appealAnswered, setAppealAnswered] = useState(() => recalledAppealResponse(eventId));
  /** Report and hide-for-me on the "Just now" rows (GH-958). */
  const giftReporting = useReporting(eventId);
  /**
   * The server's own sentence after a one-tap pledge (owner 2026-10-10), verbatim on the card:
   * "nothing has been charged" is a promise about money and belongs to the system of record,
   * never to this bundle. Nil until a pledge is recorded from this card.
   */
  const [appealConfirmation, setAppealConfirmation] = useState<string | null>(null);
  /** The server's words when a one-tap answer was refused — shown in the promise (warning) tone. */
  const [appealNotice, setAppealNotice] = useState<string | null>(null);
  /** The server refused a one-tap pledge once: the next tap takes the existing flow instead. */
  const oneTapRefused = useRef(false);
  const [infoOpen, setInfoOpen] = useState(false);
  /** Bumped when the at-event location check opens this event: every read starts again. */
  const [reload, setReload] = useState(0);
  /*
    Whether this donor already holds admission here (owner-directed 2026-09-02).

    Three values, and the third one matters: `true` hides the row, `false` shows it, and **`null`
    means unknown** — no session, or the wallet read failed — which also shows it. Offering a
    second purchase to somebody who already bought is a mistake they can see and back out of;
    hiding the only way to buy from somebody who has none is one they cannot.
  */
  const [ticketed, setTicketed] = useState<boolean | null>(null);

  /*
    The arrival marker, read once at mount.

    The owner's rule: scanning the code in the hall asks the donor to check in first; arriving from
    the directory does not. `?checkin=1` is that marker, and it is **not** `?join=` — `Home.tsx`
    already consumes and strips that parameter for a different journey, and stacking a second
    meaning on it is how one of the two quietly stops working.

    What it does here is hoist the check-in card to the top of the stack, not bar the screen. A
    blocking gate would have to run the whole phone-and-code journey inline, which is what
    `/c/<eventId>` exists for, and it would put an identity step in front of a screen whose Give
    button is deliberately account-free.
  */
  const arrivedForCheckIn = useRef(
    new URLSearchParams(window.location.search).get("checkin") === "1",
  ).current;
  /**
   * The starting amount a button, link or QR code from the console's Buttons & links tool put in
   * the address (GH-852), or null. A hint only: Give carries it to checkout as `?amount=`, where
   * it preselects and the server prices it like any other amount.
   */
  const arrivedAmount = useRef(readCheckoutHints(window.location.search).amountMinor).current;

  /* -------------------------------------------------------------- identity */

  useEffect(() => {
    let live = true;
    setLoadFailure(null);
    fetchEventPage(eventId)
      .then((value) => {
        if (!live) return;
        /*
          A general fund is not a happening (migration 0026, `directory-sections.ts`): no room to
          arrive at, no admission to sell, nothing running. It is where a gift goes, which is
          what the organization's page is for — so this is the organization's page, and
          `replace` rather than `assign` keeps the back button pointing at wherever the donor
          actually came from rather than at a redirect they never saw.
        */
        if (value.event.isGeneralFund) {
          // A button's amount (GH-852) goes along as the same hint; the tag is already kept.
          window.location.replace(
            `/o/${encodeURIComponent(value.organization.id)}${checkoutHints({
              amountMinor: arrivedAmount,
              designationId: null,
            })}`,
          );
          return;
        }
        setPage(value);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        // `findPublicEvent` returns any row whatever its status, so a 404 here means there is
        // genuinely no such event — a mistyped code rather than one that has not started.
        setLoadFailure(
          !(caught instanceof ApiError)
            ? "unavailable"
            : caught.code === "not_found"
              ? "not_found"
              : // Archived (GH-863): the event was public and was put away. Not an error.
                caught.code === "event_unavailable"
                ? "ended"
                : // GH-871b: membership is on and this visitor has no way in yet.
                  isEventAccessRequired(caught)
                  ? "access"
                  : "unavailable",
        );
      });
    return () => {
      live = false;
    };
  }, [eventId, arrivedAmount, reload]);

  /* ------------------------------------------------------------------ money */

  useEffect(() => {
    let live = true;
    const read = () => {
      fetchGoal(eventId).then(
        (value) => {
          if (!live) return;
          setGoal(value);
          // Recovery is the point: a blip that put the notice up must take it down again on the
          // next good read, four seconds later, without the donor doing anything.
          setGoalFailure(false);
        },
        (caught: unknown) => {
          // GH-871b: not open to this visitor — the page shows "Opens at the event"; asking again
          // every four seconds would only be refused again.
          if (isEventAccessRequired(caught)) {
            clearInterval(timer);
            return;
          }
          /*
            A failed poll never erases the last good total, and never empties the page.

            No `not_found` branch here. Whether this event exists was already answered by
            `fetchEventPage` above — which 404s for an unknown id and for the statuses the server
            will not advertise — so a 404 from the projection can only mean the total is not
            readable yet, which is what the notice says.
          */
          if (!live) return;
          setGoalFailure(true);
        },
      );
    };
    // The timer first, so a refusal on the very first read can stop it.
    const timer = setInterval(read, GOAL_POLL_MS);
    read();
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [eventId, reload]);

  /* ------------------------------------------------------------- engagement */

  /*
    The projection's status when it has one, the page payload's when it does not. Reading only the
    projection meant an unreachable total also silenced every prompt, so a hall whose money read
    was blipping lost its poll and its raffle too.
  */
  const isLive = (goal?.eventStatus ?? page?.event.status) === "live";

  useEffect(() => {
    // The public engagement route answers `not_found` for anything that is not live, so asking
    // before then would be a request whose only possible answer is an error. iOS guards the same
    // call the same way in `EventModel.refreshPrompts`.
    if (!isLive) return;
    let live = true;
    const read = () => {
      fetchEngagement(eventId).then(
        (loaded) => live && setPrompts(loaded),
        // A prompt list that will not load leaves the rest of the screen alone: the total, the
        // give button and the way back are all still true without it.
        () => undefined,
      );
    };
    read();
    const timer = setInterval(read, PROMPT_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [eventId, isLive]);

  /* ---------------------------------------------------------------- session */

  useEffect(() => {
    const stored = storedToken();
    if (!stored) return;
    let live = true;
    setToken(stored);
    fetchDonorSession(stored).then(
      (value) => live && setSession(value),
      // Being unrecognised is ordinary, not an error: an expired token means the donor is a
      // stranger again, and a stranger can still read this page and still give.
      () => live && setToken(null),
    );
    return () => {
      live = false;
    };
  }, []);

  /*
    The wallet, read once per session and only to answer one question.

    Not polled. Admission is bought on another screen and the browser comes back to this one on
    return, so a timer would re-ask a question whose answer cannot change while this page is open.
    A rejected read leaves `ticketed` null, which is "unknown" and keeps the row — see the state's
    own note.
  */
  useEffect(() => {
    if (token === null) return;
    let live = true;
    fetchDonorTicketedEventIds(token).then(
      (held) => live && setTicketed(held.has(eventId)),
      () => live && setTicketed(null),
    );
    return () => {
      live = false;
    };
  }, [token, eventId]);

  /* ----------------------------------------------------------------- actions */

  const checkIn = useCallback(() => {
    if (!token) return;
    setPresenceFailure(null);
    setPresenceBusy(true);
    donorCheckIn(token, eventId)
      .then(setSession)
      .catch((caught: unknown) => {
        // GH-883: the check-in page owns "verify your mobile", then posts this same arrival.
        if (isPhoneVerificationRequired(caught)) {
          window.location.assign(`/c/${encodeURIComponent(eventId)}`);
          return;
        }
        setPresenceFailure(
          caught instanceof DonorApiError ? caught.message : "Could not check you in.",
        );
      })
      .finally(() => setPresenceBusy(false));
  }, [token, eventId]);

  const checkOut = useCallback(() => {
    if (!token) return;
    setPresenceFailure(null);
    setPresenceBusy(true);
    donorCheckOut(token, eventId)
      .then(setSession)
      .catch((caught: unknown) =>
        setPresenceFailure(
          caught instanceof DonorApiError ? caught.message : "Could not check you out.",
        ),
      )
      .finally(() => setPresenceBusy(false));
  }, [token, eventId]);

  /**
   * Open the give screen — and say which of the two buttons opened it.
   *
   * Both buttons mint the same signed context, because they are the same gift decision at
   * different levels of commitment and the server is what governs either one. What differs is the
   * screen they arrive at: `?intent=pledge` makes the give screen lead with the promise instead of
   * the payment sheet, which is the whole point — until 2026-09-02 "Pledge for later" opened a
   * payment screen, which made the label untrue.
   *
   * The parameter is a **presentation hint and nothing else**. It is not signed and does not need
   * to be: `Checkout` ignores it wherever a pledge is not on offer, no money moves because of it,
   * and the amount, the quote and the promise itself all remain the server's. Forging it re-orders
   * two buttons on a screen the forger is already looking at.
   */
  const open = useCallback(
    (intent: "give" | "pledge") => {
      setGiveFailure(null);
      setGiving(true);
      fetchDonationLink(eventId)
        .then((link) =>
          window.location.assign(
            `/g/${encodeURIComponent(link.token)}${
              intent === "pledge"
                ? "?intent=pledge"
                : // A button or link's starting amount (GH-852): a preselection the server prices.
                  checkoutHints({ amountMinor: arrivedAmount, designationId: null })
            }`,
          ),
        )
        .catch(() => {
          // Never an error about payment: nothing was attempted and nothing was charged. The donor
          // is told the truth — this did not open — and the button becomes pressable again.
          setGiveFailure("That didn't open — try again.");
          setGiving(false);
        });
    },
    [eventId, arrivedAmount],
  );

  /**
   * Answer the appeal (GH-937; one tap since the owner's 2026-10-10 note, the twin of iOS
   * `EventRoomView.oneTapPledge`).
   *
   * **Pledge**, for a signed-in donor whose profile holds a verified email or (GH-939) a verified
   * phone, completes right here:
   * the rung is quoted at its own amount with the rung on the quote — the **server** holds every
   * refusal, exactly as on the checkout — and the pledge is recorded against the session's own
   * verified contact, answered with the server's sentence verbatim. Anyone the server cannot act
   * for lands on the existing checkout flow, configured exactly as before — the fallback, not the
   * path. On a verified *phone* the body names no address at all (GH-939): `donorProfileSchema`
   * carries `phoneLast4` alone — deliberately — so the server resolves the session's own verified
   * contact rather than this bundle ever holding a full number.
   *
   * The green **Give** keeps its GH-937 behaviour: donor-web has no saved-method one-tap charge
   * machinery, so it tees up checkout at the rung's amount (`intent=pledge` stays the existing
   * presentation hint on the pledge fallback).
   */
  const respondToAppeal = useCallback(
    (intent: "give" | "pledge") => {
      const rung = goal?.appeal?.currentRung;
      if (!rung) return;
      setGiveFailure(null);
      setAppealNotice(null);
      setGiving(true);
      const hints = checkoutHints({
        amountMinor: rung.amount.amountMinor,
        designationId: null,
        appealRungId: rung.id,
      });
      const checkoutUrl = (contextToken: string) =>
        `/g/${encodeURIComponent(contextToken)}${hints}${
          intent === "pledge" ? "&intent=pledge" : ""
        }`;

      const verifiedEmail = session?.profile.emailVerified ? session.profile.email : null;
      // GH-939: a verified phone is enough too — the body then names no address and the server
      // resolves the session's own verified contact (this bundle never holds the full number).
      const contact: PledgeContact | null = verifiedEmail
        ? { email: verifiedEmail }
        : session?.profile.phoneVerified
          ? {}
          : null;
      if (intent !== "pledge" || !token || !contact || oneTapRefused.current) {
        fetchDonationLink(eventId)
          .then((link) => window.location.assign(checkoutUrl(link.token)))
          .catch(() => {
            // Same truth as Give above: nothing was attempted and nothing was charged.
            setGiveFailure("That didn't open — try again.");
            setGiving(false);
          });
        return;
      }

      void (async () => {
        try {
          const link = await fetchDonationLink(eventId);
          let quote;
          try {
            // The rung's own amount, priced by the server with the rung on the quote — a pledge
            // carries no cost support, and the client computes nothing (CLAUDE.md's amount rule).
            quote = await createQuote(
              {
                contextToken: link.token,
                amountMinor: rung.amount.amountMinor,
                methodClass: "card",
                costSupportSelected: false,
                recognition: { mode: "anonymous" },
                appealRungId: rung.id,
              },
              token,
            );
          } catch (caught: unknown) {
            if (caught instanceof ApiError) {
              // The room moved, or the appeal closed, between the tap and the quote: the server's
              // words on the card, in the promise tone, and the buttons keep asking at whatever
              // rung the poll now shows. Never a dead end.
              setAppealNotice(caught.message);
              setGiving(false);
              return;
            }
            throw caught;
          }
          try {
            const name = session?.profile.displayName?.trim();
            const pledge = await createPledge(
              {
                quoteToken: quote.quoteToken,
                displayedAmount: quote.donation,
                // No challenge in the body: the server resolves the proof from the session's own
                // verified contacts, or refuses — this browser asserts nothing (api.ts). A verified
                // email is named as before (GH-938); a phone-only donor names nothing (GH-939).
                ...contact,
                ...(name ? { displayName: name } : {}),
              },
              token,
            );
            const answer = {
              kind: "pledged" as const,
              rungId: rung.id,
              amount: quote.donation,
            };
            rememberAppealResponse(eventId, answer);
            setAppealAnswered(answer);
            // The server's sentence, verbatim: it is the system of record's promise that nothing
            // was charged, and this bundle never rewrites it.
            setAppealConfirmation(pledge.summary);
            setGiving(false);
          } catch (caught: unknown) {
            if (caught instanceof ApiError) {
              // The server wants more than the session can prove (a stale contact, say): its
              // words stay on the card in the warning tone, and the next tap takes the existing
              // flow — the pledge screen can send a code. Never a dead end, and the refusal is
              // read before anything moves.
              oneTapRefused.current = true;
              setAppealNotice(caught.message);
              setGiving(false);
              return;
            }
            throw caught;
          }
        } catch {
          setGiveFailure("That didn't open — try again.");
          setGiving(false);
        }
      })();
    },
    [eventId, goal, session, token],
  );

  /* ------------------------------------------------------------- early exits */

  if (loadFailure === "not_found") {
    return (
      <main className="card card--landing">
        <p className="notice notice--error" role="alert">
          This event could not be found.
        </p>
        <a className="landing__quiet" href="/">
          Back to the directory
        </a>
      </main>
    );
  }

  if (loadFailure === "access") {
    return (
      <EventOpensAtEvent eventId={eventId} onOpened={() => setReload((count) => count + 1)}>
        <EventJoinCard
          joinToken={joinToken}
          token={token}
          join={eventJoin}
          onSession={(issued) => {
            setToken(issued.token);
            setSession(issued);
          }}
        />
      </EventOpensAtEvent>
    );
  }

  if (loadFailure === "ended") {
    // The calm empty state, as on a closed pay link: a heading, one quiet line, the way back.
    return (
      <main className="card card--landing">
        <h1>This event has ended</h1>
        <p className="muted">It is no longer available.</p>
        <a className="landing__quiet" href="/">
          Back to the directory
        </a>
      </main>
    );
  }

  if (loadFailure === "unavailable") {
    return (
      <main className="card card--landing">
        {/* Not "check your connection" and not "the server is down": this bundle cannot tell the
            two apart, and naming the wrong one sends a donor to fix something that is not broken. */}
        <p className="notice notice--error" role="alert">
          This page could not be loaded. Try again, or search the directory.
        </p>
        <a className="landing__quiet" href="/">
          Back to the directory
        </a>
      </main>
    );
  }

  if (page === null) {
    return (
      <main className="card card--landing">
        <p className="notice">Loading…</p>
      </main>
    );
  }

  /* ------------------------------------------------------------------ render */

  const { organization, event } = page;
  const status = goal?.eventStatus ?? event.status;
  const subtitle = echoes(event.purpose, event.name)
    ? echoes(event.description, event.name)
      ? null
      : event.description
    : (event.purpose ?? event.description);
  const finished = FINISHED_STATUSES.has(status);
  const givable = organization.canAcceptDonations && GIVABLE_STATUSES.has(status);
  /*
    After the event a donor can still promise (owner-directed 2026-10-01, GH-800). Ended or
    archived only — never suspended — and the give screen is the server's: it offers the pledge
    rail alone there, or refuses if the deployment takes no pledges.
  */
  const pledgeAfter =
    organization.canAcceptDonations && (status === "ended" || status === "archived");
  const running = status === "live";
  const startsLabel = startsAt(event.startsAt);
  /*
    Offered only to somebody who has not already bought (owner-directed 2026-09-02).

    Two conditions, and they answer different questions. `page.tickets` is the *organization's*:
    the server says whether there is a tier somebody could buy right now, and it is null when sales
    are closed, sold out, or the event cannot process an order. `ticketed` is this *donor's*, and
    only `true` suppresses the row — `null` is "we do not know", which shows it, because a donor who
    has none must always be able to find the way to buy.
  */
  const ticketsOffered = page.tickets !== null && ticketed !== true;
  // A hybrid event's online link (GH-866), while it is announced or running.
  const onlineUrl =
    status === "published" || status === "live" ? drawableOnlineUrl(event.onlineUrl) : null;
  /*
    Presence is three states and not a boolean, because the session names only the *latest*
    check-in and a donor may be at more than one live event in an evening (iOS
    `EventModel.Presence`). "Checked in somewhere else" is offered "Check in here" and is never
    switched automatically — `DonorPanel`'s rule is that a page may fill an *empty* check-in and
    never overwrite one.
  */
  const here = session?.checkIn?.eventId === eventId;
  const checkedInAt = here ? session?.checkIn?.checkedInAt : undefined;

  return (
    <main className="card card--landing eventpage">
      <a className="link eventpage__back" href="/">
        ‹ Directory
      </a>

      {/* The organization row: the mark and the name are a link to the organization's own page,
          and the ⓘ is a separate control beside it. They cannot be one control — a link that
          sometimes opens a panel is a link a person stops trusting — and the ⓘ must not be nested
          inside the anchor, which would make one tap ambiguous between two destinations. */}
      <header className="eventpage__org">
        <a className="eventpage__orglink" href={`/o/${encodeURIComponent(organization.id)}`}>
          <OrgMark
            displayName={organization.displayName}
            logoUrl={organization.logoUrl}
            className="eventpage__mark"
            size={44}
          />
          <span className="eventpage__ident">
            <span className="eventpage__orgname" dir="auto">
              {organization.displayName}
            </span>
            {/* The server's own verification wording, never a badge and never a sentence composed
                here — it is a statement about a registry record. */}
            <span className="eventpage__meta">
              {[organization.verificationLabel, organization.locality].filter(Boolean).join(" · ")}
            </span>
          </span>
        </a>
        <button
          type="button"
          className="fineprint__icon"
          aria-label="About this organization"
          aria-expanded={infoOpen}
          onClick={() => setInfoOpen(true)}
        >
          <InfoIcon />
        </button>
        {/* GH-864e: hidden while membership is off; a member's link carries their share code. */}
        <ShareIconButton target={{ eventId }} name={event.name} />
      </header>
      {infoOpen && (
        <OrgInfoPanel organizationId={organization.id} onDismiss={() => setInfoOpen(false)} />
      )}

      <h1 className="landing__event eventpage__name" dir="auto">
        {event.name}
      </h1>
      <EventJoinCard
        joinToken={joinToken}
        token={token}
        join={eventJoin}
        onSession={(issued) => {
          setToken(issued.token);
          setSession(issued);
        }}
      />
      {/*
        Dropped when it only repeats the heading. An organizer who names the event and its purpose
        the same way — which the console permits, and which seeded rows do — otherwise gets the
        same words twice, once large and once small, which reads as a rendering fault rather than
        as an organization with a short answer.
      */}
      {subtitle !== null && (
        <p className="landing__about" dir="auto">
          {subtitle}
        </p>
      )}

      {goal && (
        <section className="landing__goal eventpage__goal">
          <p className="landing__figure eventpage__figure">
            {/*
              The headline the room watches, folded by exactly the rule the projector uses so a
              donor looking from here to the wall and back does not see two different nights. A
              goal of zero draws the received figure and no "of $X": a bar and a target nobody set
              would be a claim either way it painted.
            */}
            <strong>{formatMoney(headline(goal))}</strong>
            {BigInt(goal.goal.amountMinor) > 0n && <span> of {formatMoney(goal.goal)}</span>}
          </p>
          <GoalBar goal={goal} />
          {/*
            Received and pledged named separately and never added together. The figure above may
            already fold promises in — the operator's choice, rendered rather than invented — and
            folding is only honest while the parts stay visible underneath. A screen that lost
            this line would be claiming money had arrived (CLAUDE.md, ADR 0012).
          */}
          <p className="landing__split eventpage__split">
            <span>
              Received {formatMoney(goal.received)}
              {BigInt(goal.pledged.amountMinor) > 0n && <> · Pledged {formatMoney(goal.pledged)}</>}
            </span>
            {/* Age is always visible (SPEC.md §7.2). It no longer turns into "Catching up…" when
                the server flags the projection stale (owner, build 27, GH-817): the flag lit on
                figures that were exactly right. `docs/evidence/GH-817.md` has why. */}
            <span className="eventpage__age">{freshness(goal)}</span>
          </p>
        </section>
      )}

      {/*
        Only when there is no figure at all. Once a total has been read, a later failure leaves the
        last good one on screen with its age beside it — a number that admits it is forty seconds
        old beats a hall with no number.
      */}
      {goal === null && goalFailure && (
        <p className="notice">
          The total isn&rsquo;t loading right now. Everything else still works.
        </p>
      )}

      {/*
        The live appeal (GH-937), while the server publishes one. `goal.appeal` is non-null only
        for a running, unhidden appeal (GH-932 gates it server-side), and it arrives on the same
        four-second goal read as the total — the card itself fetches nothing (GH-919). Gated on
        `givable` too, because both of its buttons mint a checkout the server would otherwise
        refuse. It re-renders at whatever rung the room is at on every poll, so a ladder that
        moves on simply moves this card with it.
      */}
      {givable && goal?.appeal && (
        <AppealCard
          appeal={goal.appeal}
          answered={appealAnswered}
          confirmation={appealConfirmation}
          notice={appealNotice}
          busy={giving}
          onRespond={respondToAppeal}
        />
      )}

      {/*
        Give and Pledge directly under the figure they change, above the cards (owner-directed
        2026-09-16: *"be only one click away from give page"*). They sat at the foot of the page,
        under every prompt and the check-in card, which on a busy night put the one action this
        page exists for below the fold — one click away in principle and a scroll away in fact.
      */}
      {givable && (
        <>
          {/*
            A button and not a link: there is no destination to fall back to. The give page is
            reached through a token only the server can sign (SPEC.md §5.1), and this page is
            already the event page, so an href here would point at itself.

            Nothing about this is gated on a session. The account-free first gift is the founding
            promise of the product, and check-in is the only thing on this screen that asks who
            somebody is.
          */}
          <button
            type="button"
            className="pay landing__give"
            disabled={giving}
            onClick={() => open("give")}
          >
            {giving ? "Opening…" : "Give"}
          </button>
          {/*
            "Pledge for later" stood here until GH-809 (owner, GH-793: "Remove the separate pledge
            button and just leave it inside the payment types menu"). The give screen's method
            sheet lists Pledge wherever the server offers it, so one Give is the whole decision.
          */}
          {/* "No account needed" stood here until GH-802 (owner, TestFlight build 24: remove the
              no-sign-in label from every give surface). Giving still asks for nothing. */}
        </>
      )}
      {giveFailure && (
        <p className="notice notice--error" role="alert">
          {giveFailure}
        </p>
      )}
      {status === "draft" || status === "reviewable" ? (
        <p className="notice">Not running yet</p>
      ) : null}
      {/*
        Not started yet (GH-800): an announced event takes a gift, and says when it begins. Never
        drawn as live — no live word, no live styling — because it is not on.
      */}
      {status === "published" && startsLabel !== null && (
        <p className="eventpage__starts">Starts {startsLabel}</p>
      )}
      {/*
        Suspended is not "ended": it is withdrawn, for a reason this page does not state (SPEC.md
        §6.4), so it says the neutral thing and nothing about the event's history (GH-809).
      */}
      {status === "suspended" && <p className="notice">Not available</p>}
      {finished && status !== "suspended" && (
        <p className="notice">
          {pledgeAfter ? "This event has ended. You can still pledge." : "This event has ended."}
        </p>
      )}
      {pledgeAfter && (
        <button
          type="button"
          className="pay pay--secondary landing__quiet"
          disabled={giving}
          onClick={() => open("pledge")}
        >
          Pledge
        </button>
      )}

      {/* Every offer, one shape. Nothing is drawn here that the server would refuse: the whole
          list is empty for an event that is not live, apart from tickets, which have their own
          gate on the payload. */}
      {(running || ticketsOffered || onlineUrl !== null) && (
        <ul className="actioncards">
          {running &&
            prompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                eventId={eventId}
                token={here ? token : null}
                expanded={openPromptId === prompt.id}
                onToggle={() =>
                  // Single-open, not all-open. Four expanded cards would push Give below the fold
                  // and would poll the detail route four times every five seconds from a phone on
                  // a hall's wifi; only the open one polls.
                  setOpenPromptId(openPromptId === prompt.id ? null : prompt.id)
                }
              />
            ))}

          {/*
            Trivia (GH-912): right after the engagement prompts it sits beside, and before the
            check-in/online/ticket cards, which are not things a donor plays. It needs no `<li>`
            here — `EventActionCard` already wraps every card's row in one, and the gate draws
            nothing at all (not even an empty row) when no round is live, same as an empty
            `prompts` list.

            Gated on `running` alone, never on `here` or a session: check-in happens as a side
            effect of taking part, so a card gated on a check-in was unreachable whenever trivia
            was the only live thing. `TriviaGate` owns the three states — the checked-in card,
            the join-checks-you-in form, and the signed-out teaser.
          */}
          {running && (
            <TriviaGate eventId={eventId} token={token} here={here} onSession={setSession} />
          )}

          {/*
            Only until it is done (owner-directed 2026-09-02).

            The card used to stay, turned around, reading "You are checked in — Check out". That
            spends a full-width row of a phone screen on a question the donor has already answered,
            in the stack whose whole purpose is to hold the things still asking something of them.
            Checking out is real and still supported — the server deletes the row — but it belongs
            where a donor goes looking for it rather than in front of somebody who just arrived.

            The confirmation is not lost: `ReactionBar` appears directly below on exactly the same
            condition, so the screen visibly changes shape when the check-in lands.
          */}
          {running && !here && (
            <EventActionCard
              glyph={<PersonIcon />}
              live={arrivedForCheckIn}
              label="In the room tonight?"
              failure={presenceFailure}
              action={
                token ? (
                  <button
                    type="button"
                    className="link actioncard__action"
                    disabled={presenceBusy}
                    onClick={checkIn}
                  >
                    {presenceBusy ? "Checking in…" : "Check in"}
                  </button>
                ) : (
                  // No session yet: checking in needs a phone and a code, and that journey lives
                  // at `/c/<eventId>`. A link rather than a button, because it is a destination.
                  <a className="link actioncard__action" href={`/c/${encodeURIComponent(eventId)}`}>
                    Check in
                  </a>
                )
              }
            />
          )}

          {onlineUrl !== null && (
            <EventActionCard
              glyph={<LocationIcon />}
              label="Also online"
              action={
                <a
                  className="link actioncard__action"
                  href={onlineUrl}
                  target="_blank"
                  rel={ONLINE_LINK_REL}
                >
                  Join online
                </a>
              }
            />
          )}

          {ticketsOffered && page.tickets !== null && (
            <EventActionCard
              glyph={<TicketIcon />}
              label={`Tickets from ${formatMoney(page.tickets.from)}`}
              action={
                <a
                  className="link actioncard__action"
                  href={`/tickets/${encodeURIComponent(eventId)}`}
                >
                  Get tickets
                </a>
              }
            />
          )}
        </ul>
      )}

      {/*
        The most recent gifts, newest first (GH-889) — the projection the big screen's "Just now"
        list reads, rendered phone-sized. Every row is a `PublicGift`: no donation id, no donor id,
        and "Anonymous" is a constant, not a lookup. A gift's note appears only when the server
        sent one — the donor asked for the screen and a host approved it (GH-887) — and it is
        drawn as plain text under the row, never markup. Nothing is drawn when the night has no
        gifts yet: an empty frame would be furniture, not news.
      */}
      {goal && goal.recentGifts.length > 0 && (
        <section className="eventpage__gifts" aria-label="Recent gifts">
          <h2 className="eventpage__gifts-title">Just now</h2>
          {/* `aria-live="polite"` so a reader hears a new gift without interruption, and
              `aria-atomic="false"` so it reads the row that changed rather than all five. */}
          <ul className="eventpage__gifts-list" aria-live="polite" aria-atomic="false">
            {goal.recentGifts
              .filter((gift) => !gift.reportRef || giftReporting.isShown(gift.reportRef))
              .map((gift) => (
                <li key={gift.sequence} className="eventpage__gift">
                  <span className="eventpage__gift-name" dir="auto">
                    {gift.displayName ?? "Anonymous"}
                  </span>
                  <span className="eventpage__gift-amount">
                    {formatMoney(gift.amount)}
                    {/* A pledge is a promise and is labelled as one (ADR 0012). */}
                    {gift.kind === "pledged" && (
                      <span className="eventpage__gift-tag">pledged</span>
                    )}
                  </span>
                  {gift.note !== null && gift.note !== undefined && gift.note !== "" && (
                    <span className="eventpage__gift-note" dir="auto">
                      {gift.note}
                    </span>
                  )}
                  {gift.reportRef && (
                    <ReportMenu
                      eventId={eventId}
                      target={{ kind: "gift", ref: gift.reportRef }}
                      itemKey={gift.reportRef}
                      onReported={giftReporting.onReported}
                    />
                  )}
                </li>
              ))}
          </ul>
          <ReportNotice thanks={giftReporting.thanks} />
        </section>
      )}

      {/* Who is speaking (GH-862): the organizer's speakers, with photos where they added one. */}
      <Speakers speakers={page.speakers} />

      {/* Only for somebody who is actually in the room, which is also the only case the server
          accepts — `requireCheckedIn` guards the reaction route. */}
      {running && here && token && <ReactionBar token={token} eventId={eventId} />}

      {/*
        The presence line: a confirmation, and the way back out.

        This is what is left of the check-in card once it stops asking (owner-directed
        2026-09-02). It is a line rather than a card on purpose — the card stack is for the things
        still asking something of the donor, and this asks nothing — but leaving *is* real, the
        server deletes the row rather than flagging it, and a room somebody has left must not keep
        counting them. Removing the only way out along with the question would have made check-in
        a one-way door.
      */}
      {running && here && (
        <p className="eventpage__presence">
          <span>
            You&rsquo;re checked in
            {checkedInAt ? ` since ${new Date(checkedInAt).toLocaleTimeString()}` : ""}
          </span>
          <button type="button" className="link" disabled={presenceBusy} onClick={checkOut}>
            Check out
          </button>
        </p>
      )}
      {presenceFailure && here && (
        <p className="notice notice--error" role="alert">
          {presenceFailure}
        </p>
      )}

      {!organization.canAcceptDonations && (
        <Explain className="notice" label="Not set up yet" title="Why you can't give here">
          <p>{"This organization hasn't set up giving on GiveHere yet."}</p>
        </Explain>
      )}
    </main>
  );
}

/**
 * When an announced event begins, in the reader's own locale and zone — "Oct 4, 7:00 PM" — or null
 * for an undated one, which says nothing rather than inventing a date.
 */
function startsAt(value: string | null): string | null {
  if (value === null) return null;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

/**
 * How old the number is, in the words the owner's design asks for: *"2s ago"*.
 *
 * A deliberate divergence from the other two surfaces, which both print a wall-clock time
 * ("Updated 7:04:00 PM" on the projector and on iOS). That is right for a wall — nobody watches a
 * projection's clock tick, and a time stamp is what somebody glances at from the back row. It is
 * wrong in a hand: the question a donor holding a phone is actually asking is *is this number
 * current*, and an age answers it without them having to work out what time it is now.
 *
 * `goal.stale` is not read here (GH-817). It said "Catching up…" whenever the server had a write
 * to the event newer than the projection — an abandoned checkout, a cancelled wallet sheet, a
 * receipt claimed — none of which moves the total, so it read as a fault on a correct figure.
 */
function freshness(goal: GoalResponse): string {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(goal.asOf)) / 1000));
  if (seconds < 10) return "Just now";
  if (seconds < 60) return `${seconds}s ago`;
  /*
    Minutes stop being readable somewhere around an hour. A projection that has not moved since
    last week rendered as "13265 min ago" — a figure a reader has to do arithmetic on to discover
    it means nine days, which is exactly the fact the line exists to make obvious at a glance.
  */
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/**
 * One prompt as a card: the row, and the whole prompt underneath it when it is the open one.
 *
 * `token` is null unless the donor is checked in **here** — the server enforces that itself on
 * every prompt route, so this is not the protection, it is the difference between offering a
 * control and offering a refusal. The row stays visible either way: hiding the prompts from
 * somebody who has not checked in would, in iOS `EventView`'s words, *"make the room look
 * emptier than it is."*
 */
function PromptCard({
  prompt,
  eventId,
  token,
  expanded,
  onToggle,
}: {
  prompt: EngagementPromptWire;
  eventId: string;
  token: string | null;
  expanded: boolean;
  onToggle: () => void;
}) {
  const closed = prompt.status === "closed";
  const glyph =
    prompt.kind === "poll" ? (
      <PollIcon />
    ) : prompt.kind === "raffle" ? (
      <TicketIcon />
    ) : prompt.kind === "question" ? (
      <QuestionIcon />
    ) : (
      <CommentIcon />
    );

  /* The verb, in the language of the thing being done. Never the kind's name: the row is titled
     with the organizer's own words and a second line reading "Poll" under a poll is a wasted
     line — the compaction lesson iOS's `PromptRow` already carries. */
  const verb =
    prompt.kind === "poll"
      ? "Vote"
      : prompt.kind === "raffle"
        ? "Enter"
        : prompt.kind === "question"
          ? "Ask"
          : "Write";

  return (
    <EventActionCard
      glyph={glyph}
      live={!closed}
      label={prompt.title}
      meta={[prompt.prize, closed ? "Closed" : null, prompt.resultSummary]
        .filter(Boolean)
        .join(" · ")}
      action={
        token === null && !closed ? (
          // The action says what has to happen first, rather than disappearing or lying about
          // being available. Acting on a prompt needs a check-in; reading one never does.
          <span className="actioncard__action actioncard__action--quiet">
            Check in to take part
          </span>
        ) : token === null ? undefined : (
          <button type="button" className="link actioncard__action" onClick={onToggle}>
            {expanded ? "Close" : closed ? "See results" : verb}
          </button>
        )
      }
    >
      {expanded && token !== null && (
        <PromptView
          token={token}
          prompt={prompt}
          onBack={onToggle}
          backLabel="Close"
          showTitle={false}
          eventId={eventId}
        />
      )}
    </EventActionCard>
  );
}
